<script setup lang="ts">
/**
 * Все записи повторяющейся встречи (макет 12-23, DEV-2185).
 *
 * До неё история серии была доступна только по одному созвону за раз: открыть
 * встречу, прочитать конспект, вернуться в список, найти предыдущую. За
 * «что мы решали на этих стендапах» человек платил десятком переходов.
 *
 * Панель открывается из строки серии и показывает её созвоны по датам —
 * свежие сверху, с конспектом, расшифровкой и заведёнными задачами внутри.
 *
 * Серией здесь и распоряжаются (DEV-2200): у живой автор правит ближайший
 * созвон «эту и все следующие» или отменяет всю серию; у завершённой —
 * возобновляет её. До этого завершённая серия была пустым экраном без единого
 * действия, а правило повтора менялось только пересозданием.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { CalendarPlus, Pencil, Repeat, Users } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { errorMessage } from '@/api/errors'

import { listSeriesMeetings, nextOccurrence, recurrenceLabel } from '../api'
import { useMeetingStore } from '../store'

import MeetingAnswerMark from './MeetingAnswerMark.vue'
import MeetingCancelDialog from './MeetingCancelDialog.vue'
import MeetingScheduleModal from './MeetingScheduleModal.vue'
import MeetingSeriesInstance from './MeetingSeriesInstance.vue'

import type {
  MeetingFormPreset,
  MeetingSession,
  RescheduleMeetingInput,
  ScheduleMeetingInput,
} from '../api'

const props = defineProps<{ seriesRootId: string }>()

const { t, locale } = useI18n()
const toast = useToast()
const router = useRouter()
const store = useMeetingStore()

const PAGE = 20

const instances = ref<MeetingSession[]>([])
const page = ref(1)
const isLoading = ref(false)
const hasMore = ref(false)
const failed = ref(false)

const load = async (next = false) => {
  if (isLoading.value) return

  isLoading.value = true
  failed.value = false

  try {
    const wanted = next ? page.value + 1 : 1
    const rows = await listSeriesMeetings(props.seriesRootId, wanted, PAGE)

    instances.value = next ? [...instances.value, ...rows] : rows
    page.value = wanted
    // Страница пришла полной — значит дальше может быть ещё. Отдельного
    // счётчика у выдачи нет, и просить его ради кнопки незачем.
    hasMore.value = rows.length === PAGE
  } catch {
    failed.value = true
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.seriesRootId,
  () => void load(),
  { immediate: true },
)

/**
 * Созвоны сверху вниз: сначала предстоящие, потом прошедшие свежими вперёд.
 *
 * Выдача приходит отсортированной по правилам списка встреч, но панель читают
 * иначе: «что будет» и «что было» — два разных вопроса, и смешивать их в одной
 * ленте значит заставлять искать границу глазами.
 */
const sorted = computed(() =>
  [...instances.value].sort((first, second) =>
    second.scheduledStart.localeCompare(first.scheduledStart),
  ),
)

const head = computed(() => sorted.value[0] ?? null)

/** Правило повтора — подпись шапки: «Каждый пн 10:00» */
const rule = computed(() =>
  head.value ? recurrenceLabel(head.value.recurrence, head.value.scheduledStart, t) : '',
)

/** Жива ли серия: по ней панель говорит «идёт» или «завершена» */
const isActive = computed(() => sorted.value.some((meeting) => meeting.seriesActive))

/**
 * Живые серии корня: у которых есть не отменённый созвон впереди.
 *
 * seriesActive — живость корня: правка «эта и следующие» закрывает серию и
 * заводит новую с тем же корнем, и у созвона обрезанной серии он говорит
 * «жива». Вернуть такой созвон нельзя — его вхождения в правиле повтора
 * больше нет (ADR-0057, DEV-2220), — поэтому строке отдаётся живость её
 * собственной серии.
 */
const liveSeries = computed(() => {
  const now = new Date()

  return new Set(
    instances.value
      .filter(
        (meeting) =>
          meeting.seriesId &&
          meeting.meetingStatus !== 'CANCELLED' &&
          new Date(meeting.scheduledEnd) >= now,
      )
      .map((meeting) => meeting.seriesId),
  )
})

const title = computed(() => head.value?.title ?? '')

const pastCount = computed(() => head.value?.seriesPastCount ?? 0)

const when = (iso: string) =>
  iso
    ? new Date(iso).toLocaleString(locale.value, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

/** Ближайший созвон: тот, что ещё впереди и не отменён */
const next = computed(() =>
  [...sorted.value]
    .reverse()
    .find(
      (meeting) =>
        meeting.meetingStatus !== 'CANCELLED' &&
        new Date(meeting.scheduledEnd || meeting.scheduledStart) >= new Date(),
    ),
)

/** Состав серии — по ближайшему созвону, у завершённой — по последнему */
const people = computed(
  () => (next.value ?? head.value)?.participants.filter((participant) => participant.name) ?? [],
)

/** Ответы на приглашение — у живой серии: в завершённую звать уже некого */
const showsAnswers = computed(() => isActive.value && Boolean(next.value?.meetingId))

/** Серией распоряжается тот, кто её назначил */
const isAuthor = computed(() => head.value?.iAmAuthor ?? false)

const durationOf = (meeting: MeetingSession) =>
  meeting.scheduledStart && meeting.scheduledEnd
    ? Math.round(
        (new Date(meeting.scheduledEnd).getTime() - new Date(meeting.scheduledStart).getTime()) /
          60_000,
      )
    : 30

/**
 * Правка серии — форма правки ближайшего созвона с областью «эту и все
 * следующие»: серию правят целиком, а один её созвон — из самой встречи.
 */
const isEditing = ref(false)
const editForm = ref<InstanceType<typeof MeetingScheduleModal> | null>(null)

const editPreset = computed<MeetingFormPreset>(() => {
  const meeting = next.value

  return meeting
    ? {
        title: meeting.title,
        start: new Date(meeting.scheduledStart),
        durationMinutes: durationOf(meeting),
        projectId: meeting.projectId,
        recurrence: meeting.recurrence,
        isSeries: true,
        scope: 'MEETING_SCOPE_THIS_AND_FOLLOWING',
      }
    : {}
})

const save = async (input: RescheduleMeetingInput) => {
  if (!next.value) return

  try {
    await store.reschedule(next.value.id, input)
    isEditing.value = false
    toast.success(t('meetings.actions.saved'))
    await load()
  } catch (failure) {
    editForm.value?.stopSaving()
    toast.error(errorMessage(failure, t('meetings.actions.saveFailed')))
  }
}

/** Отмена серии — та же модалка с причиной, по ближайшему созвону */
const isCancelling = ref(false)

const cancelled = () => {
  isCancelling.value = false
  void load()
}

/**
 * Возобновить серию или заново запланировать её прошедший созвон: форма
 * планирования, предзаполненная этим созвоном, с корнем серии — новая встреча
 * ложится в её историю, и серия снова живая.
 */
const replanning = ref<MeetingSession | null>(null)
const replanForm = ref<InstanceType<typeof MeetingScheduleModal> | null>(null)

const replanPreset = computed<MeetingFormPreset>(() => {
  const meeting = replanning.value

  if (!meeting) return {}

  const now = new Date()
  const from = new Date(meeting.scheduledStart || now)

  // Время суток — из созвона, дата — от сегодня: следующая по правилу после
  // давно прошедшего созвона тоже давно прошла.
  from.setFullYear(now.getFullYear(), now.getMonth(), now.getDate())

  return {
    title: meeting.title,
    start: nextOccurrence(meeting.recurrence, from),
    durationMinutes: durationOf(meeting),
    projectId: meeting.projectId,
    recurrence: meeting.recurrence,
    participants: meeting.participants
      .filter((participant) => participant.userId !== meeting.authorId)
      .map((participant) => ({
        id: participant.userId,
        name: participant.name,
        hasMailbox: participant.hasMailbox,
      })),
    seriesRootId: props.seriesRootId,
  }
})

const replan = async (input: ScheduleMeetingInput) => {
  try {
    const meeting = await store.schedule(input)

    replanning.value = null
    toast.success(t('meetings.schedule.done'))
    await router.push(`/meetings/${meeting.id}`)
  } catch {
    replanForm.value?.stopSaving()
    toast.error(t('meetings.schedule.failed'))
  }
}
</script>

<template>
  <section class="spanel">
    <header class="spanel__head">
      <h1 class="spanel__title">
        <Repeat :size="18" aria-hidden="true" />
        {{ title }}
      </h1>

      <p class="spanel__meta">
        <span>{{ rule }}</span>
        <span v-if="isActive && next">{{
          t('meetings.series.nextCall', { when: when(next.scheduledStart) })
        }}</span>
        <span v-else>{{ t('meetings.series.ended') }}</span>
        <span v-if="pastCount">{{ t('meetings.series.pastCount', pastCount) }}</span>
      </p>

      <p v-if="people.length" class="spanel__people">
        <Users :size="13" aria-hidden="true" />
        <span class="spanel__people-label">{{ t('meetings.series.people') }}:</span>
        <span
          v-for="(participant, index) in people"
          :key="participant.userId"
          class="spanel__person"
        >
          {{ participant.name }}
          <MeetingAnswerMark
            v-if="showsAnswers && participant.hasMailbox && participant.userId !== head?.authorId"
            :status="participant.responseStatus"
          />
          <template v-if="index < people.length - 1">,</template>
        </span>
      </p>

      <!-- Действия — у автора и прямо в шапке: их искали именно здесь, а
           находили пустой экран (DEV-2200). -->
      <div v-if="isAuthor && head" class="spanel__actions">
        <template v-if="isActive && next">
          <button type="button" class="spanel__action" @click="isEditing = true">
            <Pencil :size="13" aria-hidden="true" />
            {{ t('meetings.actions.edit') }}
          </button>

          <button type="button" class="spanel__action" @click="isCancelling = true">
            {{ t('meetings.series.cancelSeries') }}
          </button>
        </template>

        <button
          v-else
          type="button"
          class="spanel__action spanel__action--main"
          @click="replanning = head"
        >
          <CalendarPlus :size="13" aria-hidden="true" />
          {{ t('meetings.series.resume') }}
        </button>
      </div>
    </header>

    <p v-if="failed" class="spanel__hint">{{ t('meetings.series.loadFailed') }}</p>
    <p v-else-if="isLoading && !instances.length" class="spanel__hint">
      {{ t('meetings.loading') }}
    </p>
    <p v-else-if="!instances.length" class="spanel__hint">{{ t('meetings.series.empty') }}</p>

    <template v-else>
      <!-- Завершённая серия объясняет, что делать дальше, а не просто молчит. -->
      <p v-if="!isActive" class="spanel__hint">{{ t('meetings.series.endedHint') }}</p>

      <div class="spanel__list">
        <MeetingSeriesInstance
          v-for="instance in sorted"
          :key="instance.id"
          :meeting="instance"
          :series-live="liveSeries.has(instance.seriesId)"
          @replan="replanning = $event"
        />
      </div>
    </template>

    <AppButton
      v-if="hasMore"
      variant="secondary"
      size="small"
      :disabled="isLoading"
      @click="load(true)"
    >
      {{ t('meetings.series.more2') }}
    </AppButton>
  </section>

  <MeetingScheduleModal
    v-if="isEditing"
    ref="editForm"
    mode="edit"
    :preset="editPreset"
    @close="isEditing = false"
    @save="save"
  />

  <MeetingScheduleModal
    v-if="replanning"
    ref="replanForm"
    :preset="replanPreset"
    @close="replanning = null"
    @submit="replan"
  />

  <MeetingCancelDialog
    v-if="isCancelling && next"
    :meeting="next"
    :title="t('meetings.series.cancelSeries')"
    scope="MEETING_SCOPE_THIS_AND_FOLLOWING"
    @close="isCancelling = false"
    @cancelled="cancelled()"
  />
</template>

<style scoped>
.spanel {
  display: flex;
  flex: 1;
  min-width: 0;
  /* Панель скроллится внутри себя: серия живёт месяцами, и её история не
     должна уводить список встреч за нижний край экрана. */
  min-height: 0;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding-right: 4px;
}

.spanel__head {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.spanel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-semibold);
  line-height: 26px;
}

.spanel__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.spanel__meta > span + span::before {
  margin-right: 8px;
  content: '·';
}

.spanel__people {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  line-height: 18px;
}

.spanel__people-label {
  color: var(--color-text-muted);
}

.spanel__person {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.spanel__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

/* Кнопки — как в шапке встречи: одно и то же действие должно выглядеть
   одинаково, где бы человек его ни нашёл. */
.spanel__action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  line-height: 18px;
  cursor: pointer;
}

.spanel__action:hover {
  color: var(--color-text-primary);
}

/* Возобновление — единственное действие у завершённой серии, и читается оно
   как главное. */
.spanel__action--main {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.spanel__list {
  display: flex;
  flex-direction: column;
}

.spanel__hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}
</style>
